import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useSeo } from "@/hooks/useSeo";
import { milestones, siteStats, teamMembers } from "@/mocks/site";

export default function About() {
  useSeo({
    title: "关于我们 - 樱花动漫官网",
    description:
      "樱花动漫官网是一个面向动漫爱好者的综合平台，提供动漫资料、分类筛选与行业资讯，了解我们的团队、发展历程与内容理念。",
    keywords: "关于樱花动漫,樱花动漫官网,动漫平台,动漫团队",
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">关于我们</span>
            </nav>

            <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <div>
                <span className="font-label text-[11px] uppercase tracking-[0.22em] text-primary-600">
                  About Sakura Anime
                </span>
                <h1 className="mt-3 text-3xl font-bold leading-tight text-foreground-950 md:text-[40px]">
                  为热爱动画的人，
                  <br className="hidden sm:block" />
                  认真做一个网站
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                  樱花动漫官网从一个三人维护的番剧推荐博客开始，七年之后，它变成了一个收录两千余部作品、每天更新资讯的内容平台。我们想做的事情很简单：让你在最短的时间里，找到那部愿意熬夜看完的动画。
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    开始浏览作品
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    联系我们
                  </Link>
                </div>
              </div>

              <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20illustration%20of%20a%20bright%20anime%20studio%20workspace%20with%20drawing%20tablets%2C%20bookshelves%20full%20of%20art%20books%2C%20pink%20sakura%20branches%20by%20the%20window%20and%20soft%20afternoon%20light%2C%20cozy%20detailed%20scene%2C%20high%20detail&width=1000&height=750&seq=sakura-about-hero&orientation=landscape"
                  alt="樱花动漫编辑部工作场景插画"
                  title="樱花动漫官网 · 编辑部"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-4 border-t border-background-200 pt-8 md:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <span className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">Our idea</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">我们怎么理解「动漫平台」</h2>
            </div>
            <div className="space-y-4 text-sm leading-7 text-foreground-700">
              <p>
                一个动漫站点最容易犯的错误，是把内容堆得越满越好。首页塞满海报，列表没有尽头，用户点进三层才发现这不是自己想看的东西。我们更愿意做减法：把分类做清楚，把信息写准确，让每一次点击都有明确的去处。
              </p>
              <p>
                所以站内所有作品都保留了统一的字段：年份、地区、类型、连载状态、制作公司与简介。你可以按任意组合筛选，也可以在详情页沿着标签继续往下找。相关推荐不是随机塞几部热门作品，而是根据类型重合度排序出来的。
              </p>
              <p>
                资讯频道同样如此。我们不追热点数量，只保证每条内容都有明确的来源与观点。评测写清楚喜欢和不喜欢的理由，行业观察尽量给出可验证的事实，活动信息必须带上时间与参与方式。
              </p>
              <p className="rounded-lg border border-primary-200 bg-primary-50 p-4 text-foreground-800">
                需要说明的是，本站为演示项目：播放功能仅展示剧集切换效果，不提供真实视频源；文中人物、公司与数据均为示例，仅用于呈现产品形态。
              </p>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full max-w-[1400px] px-4 md:px-8">
            <div className="max-w-2xl">
              <span className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">The team</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">四个人，和一堆没看完的番</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                团队很小，分工很杂。有人负责选题，有人负责写字，有人负责把版式调顺眼，也有人负责在深夜回复你的留言。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <div className="aspect-square w-full overflow-hidden bg-background-200">
                    <img
                      src={member.avatar}
                      alt={`${member.name} 的头像插画`}
                      title={`${member.name} - ${member.role}`}
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-bold text-foreground-950">{member.name}</h3>
                      <span className="whitespace-nowrap rounded bg-secondary-100 px-1.5 py-0.5 font-label text-[10px] text-secondary-900">
                        {member.role}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <span className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">Milestones</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">发展历程</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                没有融资故事，也没有一夜爆红，只是一步一步把想做的事做完。
              </p>
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700"
              >
                想和我们聊聊
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>

            <ol className="relative space-y-6 border-l border-background-300 pl-6 md:space-y-7">
              {milestones.map((item) => (
                <li key={item.year} className="relative">
                  <span className="absolute -left-[31px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-primary-500 bg-background-50" />
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="font-heading text-lg font-bold text-primary-600">{item.year}</span>
                    <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                  </div>
                  <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-4 pb-14 md:px-8">
          <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-4 rounded-lg bg-primary-500 px-6 py-8 text-background-50 sm:flex-row sm:items-center sm:justify-between md:px-10">
            <div>
              <h2 className="text-xl font-bold md:text-2xl">有想推荐的番，或者想提的意见？</h2>
              <p className="mt-2 text-sm text-primary-50/90">
                每一条留言我们都会看。合作、投稿、纠错，都可以从联系页开始。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
            >
              给我们留言
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}